import { TZDate, tz } from "@date-fns/tz";
import { format } from "date-fns";

/** Every date here is a calendar date with no zone of its own, so it is read and rendered in UTC. */
export const UTC = { in: tz("UTC") };

export const DateFormat = {
  US: "M/d/yyyy",
  US_SHORT: "M/d",
  ISO: "yyyy-MM-dd",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function utcMidnight(isoDate) {
  return new TZDate(`${isoDate}T00:00:00Z`, "UTC");
}

/**
 * Returns `isoDate` ("2026-03-11") as the instant at its UTC midnight.
 *
 * @param {string} isoDate
 * @returns {TZDate}
 * @throws {RangeError} when `isoDate` is not a date in that form.
 */
export function fromISODate(isoDate) {
  if (!isISODate(isoDate)) {
    throw new RangeError(`not an ISO date: ${JSON.stringify(isoDate)}`);
  }

  return utcMidnight(isoDate);
}

/**
 * Returns `isoDate` as "3/11/2026", or undefined when there is no date.
 *
 * @param {string} [isoDate]
 * @returns {string|undefined}
 */
export function asUSDate(isoDate) {
  return isoDate ? format(fromISODate(isoDate), DateFormat.US, UTC) : undefined;
}

/**
 * Returns `isoDate` ("2026-03-11") at the *viewer's* midnight, which is the shape a calendar
 * widget renders and selects. `fromISODate` gives the UTC instant instead, for comparing
 * calendar dates against one another.
 *
 * @param {string} isoDate
 * @returns {Date}
 * @throws {RangeError} when `isoDate` is not a date in that form.
 */
export function toLocalDate(isoDate) {
  const [year, month, day] = fromISODate(isoDate).toISOString().slice(0, 10).split("-").map(Number);

  return new Date(year, month - 1, day);
}

/**
 * Returns the calendar date `date` falls on in the viewer's zone, as "2026-03-11". The inverse
 * of `toLocalDate`, so a date selected in a calendar round-trips unchanged.
 *
 * @param {Date} date
 * @returns {string}
 */
export function toISODate(date) {
  return format(date, DateFormat.ISO);
}

const DEFAULT_TIME_ZONE = "UTC";

/**
 * Returns `timeZone` when this runtime knows it, otherwise `DEFAULT_TIME_ZONE`.
 *
 * An empty value resolves to `DEFAULT_TIME_ZONE` rather than to the viewer's own zone,
 * which is what the platform reads a missing `timeZone` as.
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
export function resolveTimeZone(timeZone) {
  if (!timeZone) {
    return DEFAULT_TIME_ZONE;
  }

  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

/**
 * Returns today's calendar date in `timeZone` as `YYYY-MM-DD`: at 02:00 UTC on 2026-09-17,
 * `"America/Los_Angeles"` gives `"2026-09-16"` and `"Pacific/Kiritimati"` gives `"2026-09-17"`.
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
export function todayIn(timeZone) {
  return format(new TZDate(Date.now(), resolveTimeZone(timeZone)), DateFormat.ISO);
}

/**
 * Whether `value` is a calendar date written as `YYYY-MM-DD` and naming a real day.
 *
 * `"2026-02-30"` is the shape without being a day, so the shape alone is not the test.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isISODate(value) {
  if (typeof value !== "string" || !ISO_DATE.test(value)) {
    return false;
  }

  const date = utcMidnight(value);

  // `2026-02-30` has the shape and rolls over to March, so the reading is compared back.
  return !Number.isNaN(date.getTime()) && format(date, DateFormat.ISO, UTC) === value;
}
